import { formatYearLabel } from "common/date-utils";
import { CustomColumnType } from "./grid-column-types";
import { FUND_LEVEL_PREFIX, addNumericValue, buildPivotGroupKey, getCurrency, getFieldValue, resolveDisplayField, resolvePivotFields } from "./insGrid-cashflow-pivot-utils";
const resolveMetricValue = (row, def) => {
    const actual = getFieldValue(row, def.actual);
    const projected = def.projected ? getFieldValue(row, def.projected) : undefined;
    return actual ?? projected ?? null;
};
const resolveMetricCurrency = (rows, def) => {
    for (const row of rows) {
        const currency = getCurrency(row, def.actual) ??
            (def.projected ? getCurrency(row, def.projected) : undefined);
        if (currency) {
            return currency;
        }
    }
    return undefined;
};
const fillMetricYearColumns = (cashflowMetricRow, cashflowMetricDef, sourceRows, yearKeys, metaRow, assignValue) => {
    if (cashflowMetricDef.spread) {
        const singleRow = sourceRows.find((row) => resolveMetricValue(row, cashflowMetricDef) !== null);
        const singleVal = singleRow ? resolveMetricValue(singleRow, cashflowMetricDef) : null;
        yearKeys.forEach((yKey) => {
            cashflowMetricRow[yKey] = singleVal;
        });
        return;
    }
    if (cashflowMetricDef.columnType) {
        sourceRows.forEach((row) => {
            if (!row.ReferenceDate) {
                return;
            }
            const yKey = formatYearLabel(new Date(row.ReferenceDate));
            const val = resolveMetricValue(row, cashflowMetricDef);
            cashflowMetricRow[yKey] = assignValue(cashflowMetricRow[yKey], val);
        });
        return;
    }
    const currency = resolveMetricCurrency(sourceRows, cashflowMetricDef);
    sourceRows.forEach((row) => {
        if (!row.ReferenceDate) {
            return;
        }
        const yKey = formatYearLabel(new Date(row.ReferenceDate));
        const val = resolveMetricValue(row, cashflowMetricDef);
        cashflowMetricRow[yKey] = assignValue(cashflowMetricRow[yKey], val);
    });
    yearKeys.forEach((yKey) => {
        const val = cashflowMetricRow[yKey];
        cashflowMetricRow[yKey] = val === null ? val : { amount: val, currency };
    });
};
const classifyYears = (rows, metrics, yearKeys) => {
    const actualYears = new Set();
    const projectedYears = new Set();
    const referenceMetric = metrics.find((m) => m.projected && !m.columnType);
    if (!referenceMetric) {
        return { actualYearKeys: yearKeys, projectedYearKeys: [] };
    }
    rows.forEach((row) => {
        if (!row.ReferenceDate) {
            return;
        }
        const yKey = formatYearLabel(new Date(row.ReferenceDate));
        const actualVal = getFieldValue(row, referenceMetric.field);
        if (actualVal !== null && actualVal !== undefined) {
            actualYears.add(yKey);
        }
        if (referenceMetric.projected) {
            const projectedVal = getFieldValue(row, referenceMetric.projected);
            if (projectedVal !== null && projectedVal !== undefined) {
                projectedYears.add(yKey);
            }
        }
    });
    return {
        actualYearKeys: yearKeys.filter((yKey) => actualYears.has(yKey)),
        projectedYearKeys: yearKeys.filter((yKey) => projectedYears.has(yKey) && !actualYears.has(yKey))
    };
};
const buildGroupYearValues = (groupLevelMetric, groupRows, yearKeys, metaRow, assignValue) => {
    if (!groupLevelMetric) {
        return {};
    }
    const glmDef = { actual: groupLevelMetric.field, projected: groupLevelMetric.projected };
    const tempRow = Object.fromEntries(yearKeys.map((yKey) => [yKey, null]));
    fillMetricYearColumns(tempRow, glmDef, groupRows, yearKeys, metaRow, assignValue);
    const result = {};
    yearKeys.forEach((yKey) => {
        result[`${FUND_LEVEL_PREFIX}${yKey}`] = tempRow[yKey];
    });
    return result;
};
const buildMetricRow = (metric, resolved, ctx) => {
    const metricDef = { actual: metric.field, columnType: metric.columnType, projected: metric.projected, spread: metric.spread };
    const metricRow = {
        ...resolved,
        [ctx.displayField]: resolved[ctx.displayField] || "",
        [ctx.metricLabel]: metric.name,
        type: metric.columnType ?? CustomColumnType.InsMoneyColumn,
        ...Object.fromEntries(ctx.yearKeys.map((yKey) => [yKey, null])),
        ...Object.fromEntries((ctx.fundLevelFields ?? []).map((f) => [`${FUND_LEVEL_PREFIX}${f}`, resolved[f] ?? ""])),
        ...ctx.groupYearValues,
        [`${FUND_LEVEL_PREFIX}Model`]: ctx.metaRow.FundCffModelName || "N/A",
        [`${FUND_LEVEL_PREFIX}Action`]: { assistiveText: "Change model", icon: "edit", type: "tertiary" }
    };
    fillMetricYearColumns(metricRow, metricDef, ctx.groupRows, ctx.yearKeys, ctx.metaRow, ctx.assignValue);
    if (metric.columnType) {
        ctx.actualYearKeys.forEach((yKey) => {
            metricRow[yKey] = null;
        });
    }
    return metricRow;
};
const collectRowIntoGroup = (row, groupFields, groups, yearDateMap) => {
    const groupKey = groupFields.length > 0
        ? buildPivotGroupKey(row, groupFields)
        : { fundId: "", investorId: "", representation: "_all_" };
    const existing = groups.get(groupKey.representation);
    if (existing) {
        existing.rows.push(row);
    }
    else {
        groups.set(groupKey.representation, { key: groupKey, rows: [row] });
    }
    if (row.ReferenceDate) {
        const yKey = formatYearLabel(new Date(row.ReferenceDate));
        if (!yearDateMap.has(yKey)) {
            yearDateMap.set(yKey, new Date(row.ReferenceDate).getTime());
        }
    }
};
const extractRows = (groupRows, ctx, key) => {
    const metaRow = groupRows.find((r) => r.FundCffModelId) || ctx.fundModelMap[key.fundId] || groupRows[0] || {};
    // The investorRow holds the Fund.InvestorId datapoint, which is actually the "InvestmentId" - the ID of the mapping of Investor into a Fund
    const investorRow = groupRows.find((r) => r.InvestorId) || groupRows[0] || {};
    const isGenericInvestorRow = groupRows.find((r) => r.IsGenericInvestor !== undefined || r.measures?.IsGenericInvestor !== undefined) || investorRow;
    // Whereas the investorInvestorRow holds the Investor.InvestorId datapoint, which is the ID of the Investor itself, and needed for filtering
    const investorInvestorIdRow = groupRows.find((r) => r.InvestorInvestorId) || groupRows[0] || {};
    return {
        investorInvestorIdRow,
        investorRow,
        isGenericInvestorRow,
        metaRow
    };
};
const buildGroupResult = (group, ctx) => {
    const { key, rows: groupRows } = group;
    if (!groupRows.some((r) => r.ReferenceDate)) {
        return [];
    }
    if (ctx.hasInvestorDimension && !groupRows.some((r) => r.InvestorId || r.InvestorName)) {
        return [];
    }
    const resolved = resolvePivotFields(groupRows, [ctx.displayField]);
    const { metaRow, investorRow, isGenericInvestorRow, investorInvestorIdRow } = extractRows(groupRows, ctx, key);
    Object.assign(resolved, {
        FundCffModelId: metaRow.FundCffModelId ?? "",
        FundId: key.fundId,
        InvestorId: key.investorId || investorRow.InvestorId || "",
        InvestorInvestorId: investorInvestorIdRow.InvestorInvestorId ?? "",
        IsGenericInvestor: isGenericInvestorRow.IsGenericInvestor ?? isGenericInvestorRow.measures?.IsGenericInvestor ?? false
    });
    resolveDisplayField(resolved, ctx.displayField, groupRows);
    if (ctx.groupFields.length > 0 && !resolved.FundName && !resolved[ctx.displayField]) {
        return [];
    }
    const groupYearValues = buildGroupYearValues(ctx.groupLevelMetric, groupRows, ctx.yearKeys, metaRow, ctx.assignValue);
    const rowCtx = {
        actualYearKeys: ctx.actualYearKeys,
        assignValue: ctx.assignValue,
        displayField: ctx.displayField,
        fundLevelFields: ctx.fundLevelFields,
        groupRows,
        groupYearValues,
        metaRow,
        metricLabel: ctx.metricLabel,
        yearKeys: ctx.yearKeys
    };
    return ctx.metrics.map((metric) => buildMetricRow(metric, resolved, rowCtx));
};
export const pivotRowsToMetrics = (rows, config) => {
    const { aggregation, displayField, fundLevelFields, groupFields, groupLevelMetric, metricLabel, metrics } = config;
    const isSumMode = aggregation === "sum";
    const groups = new Map();
    const yearDateMap = new Map();
    rows.forEach((row) => collectRowIntoGroup(row, groupFields, groups, yearDateMap));
    const yearKeys = [...yearDateMap.entries()]
        .sort(([, a], [, b]) => a - b)
        .map(([key]) => key);
    const hasInvestorDimension = groupFields.includes("InvestorId");
    const fundModelMap = {};
    groups.forEach((group) => {
        const modelRow = group.rows.find((r) => r.FundCffModelId);
        if (modelRow && !fundModelMap[group.key.fundId]) {
            fundModelMap[group.key.fundId] = modelRow;
        }
    });
    const assignValue = isSumMode
        ? addNumericValue
        : (current, val) => (val !== null || current === null ? val : current);
    const { actualYearKeys, projectedYearKeys } = classifyYears(rows, metrics, yearKeys);
    const groupCtx = {
        actualYearKeys,
        assignValue,
        displayField,
        fundLevelFields,
        fundModelMap,
        groupFields,
        groupLevelMetric,
        hasInvestorDimension,
        metricLabel,
        metrics,
        yearKeys
    };
    const resultRows = [...groups.values()].flatMap((group) => buildGroupResult(group, groupCtx));
    return { actualYearKeys, projectedYearKeys, rows: resultRows, yearKeys };
};
